import React, { useEffect, useState } from 'react';
import PatientInfo from './components/patient-info/patientInfo';
import { Spinner, Card } from '../../shared/custom-ui';
import { V2Button } from '../../shared/custom-ui/v2-components';
import ModernPageHeader from '../WorkListTable/components/ModernPageHeader';
import ApiServices from '../../shared/ApiServices/ApiServices';
import { toast } from 'react-toastify';
import { UserPlus, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PatientRegister() {

    const patientInfoRef = React.createRef<any>();
    const [apiLoading, setApiLoading] = useState(false);
    const [mainTabs, setMainTabs] = useState<{ id: string; label: string }[]>([]);
    const [activeTab, setActiveTab] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (mainTabs.length === 0) {
            setActiveTab(null);
        } else if (!mainTabs.some(t => t.id === activeTab)) {
            setActiveTab(mainTabs[0].id);
        }
    }, [mainTabs]);

    const saveForm = async () => {
        const currentRef = patientInfoRef.current;
        const patientInfo = currentRef?.savePatientInfo();
        if (!patientInfo?.status) return;

        setApiLoading(true);
        let patientId: string | null = null;

        try {
            await toast.promise(
                ApiServices.savePatient(patientInfo.data)
                    .then(res => {
                        // API response: { message: "Patient registered successfully", patientId: "..." }
                        patientId = res?.patientId ?? res?.PK ?? res?.pk ?? null;
                        currentRef?.resetValues();
                    })
                    .catch(err => {
                        const errorMessage = err?.response?.data?.message || 'Error in saving patient!';
                        throw new Error(errorMessage);
                    })
                    .finally(() => setApiLoading(false)),
                {
                    pending: 'Registering patient info...',
                    success: 'Patient registered successfully!',
                    error: {
                        render({ data }: any) {
                            return data.message || 'Error in saving patient!';
                        },
                    },
                }
            );

            // Navigate directly to the new patient's profile page
            if (patientId) {
                navigate(`/patients-history/${patientId}`);
            } else {
                // Fallback: go to list if patientId was not returned
                navigate('/patients-history');
            }
        } catch {
            // toast.promise already displayed the error
        }
    };

    return (
        <div className="relative">
            <ModernPageHeader
                title="Register Patient"
                subtitle="Create a new patient profile. All required fields must be completed."
                icon={<UserPlus className="w-[15px] h-[15px]" />}
                left={
                    <button
                        type="button"
                        onClick={() => navigate('/patients-history')}
                        aria-label="Return to Patient List"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            fontSize: '13px',
                            fontWeight: 500,
                            color: 'var(--v2-text-muted)',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            transition: 'color 0.15s, background 0.15s',
                            lineHeight: 1,
                            whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={e => {
                            (e.currentTarget as HTMLButtonElement).style.color = 'var(--v2-text-primary)';
                            (e.currentTarget as HTMLButtonElement).style.background = 'var(--v2-bg-card-inner)';
                        }}
                        onMouseLeave={e => {
                            (e.currentTarget as HTMLButtonElement).style.color = 'var(--v2-text-muted)';
                            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                        }}
                    >
                        <ArrowLeft style={{ width: 13, height: 13 }} />
                        Patient List
                    </button>
                }
                actions={
                    <V2Button
                        onClick={saveForm}
                        variant="primary"
                        size="small"
                        disabled={apiLoading}
                        aria-label="Save patient registration form"
                        rightIcon={
                            !apiLoading ? (
                                <svg
                                    className="h-3.5 w-3.5"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    aria-hidden="true"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                            ) : undefined
                        }
                    >
                        {apiLoading ? <Spinner size="sm" position="inline" /> : 'Save Form'}
                    </V2Button>
                }
            />

            <Card variant="default" className="overflow-visible p-8">
                <PatientInfo ref={patientInfoRef} />
            </Card>
        </div>
    );
}
